/**
 * FCC Import Management admin page
 *
 * Three explicit sync operations:
 *   1. Check for Updates  — fcc_sync.py freshness check; skips if unchanged
 *   2. Re-import from Cache — fcc_import.py --cached-only; uses local ZIPs
 *   3. Download & Import  — fcc_import.py --force-download; always pulls fresh
 *
 * Cache panel shows each bundle's ZIP with age/size and a delete button.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { format } from 'date-fns'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import {
  triggerImport,
  triggerSync,
  getImportStatus,
  getImportSettings,
  updateImportSettings,
  abortRunningImport,
  getCacheStatus,
  deleteCacheBundle,
  loadCacheBundleFromUrl,
  refreshCacheBundle,
  getCacheBundleJob,
} from '../../services/admin'
import { RADIO_SERVICES } from '../../types'
import type { ImportLog, ImportSettings, CacheStatus, CacheBundleInfo, CacheJobStatus } from '../../types'
import Modal, { ModalFooter } from '../../components/Modal'
import { PHASE_LABELS, formatBytes, formatDuration, formatElapsed, parseServerDate } from '../../utils/importStatus'

const POLL_INTERVAL_MS = 5000
// Cache jobs (download / freshness check) run server-side and can take minutes.
const CACHE_JOB_POLL_MS = 2000

// Human-readable bundle labels for the cache panel
const BUNDLE_LABELS: Record<string, string> = {
  l_LMpriv: 'Licenses — LM Private (l_LMpriv)',
  l_LMcomm: 'Licenses — LM Commercial (l_LMcomm)',
  a_LMpriv: 'Applications — LM Private (a_LMpriv)',
  a_LMcomm: 'Applications — LM Commercial (a_LMcomm)',
}

const DAY_NAMES: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri',
}

function bundleLabel(key: string): string {
  if (BUNDLE_LABELS[key]) return BUNDLE_LABELS[key]
  // Daily delta files: l_lp_fri, l_lc_mon, a_lp_tue, a_lc_wed
  const m = key.match(/^(l|a)_(lp|lc)_([a-z]{3})$/)
  if (m) {
    const type = m[1] === 'l' ? 'License' : 'Application'
    const bundle = m[2] === 'lp' ? 'LM Private' : 'LM Commercial'
    const day = DAY_NAMES[m[3]] ?? m[3].toUpperCase()
    return `${type} Delta — ${bundle} ${day} (${key})`
  }
  return key
}

/**
 * Collapse a selection to something readable in a one-line header.
 *
 * A short list still reads best as the actual values ("CO, NE, WY"), but once the
 * client enabled all 50 states the enumerated list filled several lines and told
 * the reader nothing, so past a threshold we report the count instead.
 */
function summariseSelection(values: string[], noun: string): string {
  const n = values.length
  if (n === 0) return `no ${noun}s`
  if (n <= 6) return values.join(', ')
  return `${n} ${noun}s`
}

function statusBadge(s: ImportLog['status']) {
  if (s === 'running')
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Running</span>
  if (s === 'completed')
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Completed</span>
  if (s === 'interrupted')
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Interrupted</span>
  return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Failed</span>
}

function formatMB(bytes: number): string {
  return `${Math.round(bytes / 1024 / 1024)} MB`
}

function rowTooltip(log: ImportLog): string {
  if (log.error_message) {
    const preview = log.error_message.length > 160
      ? log.error_message.slice(0, 160) + '…'
      : log.error_message
    return `Error: ${preview}\n\nClick to view full details.`
  }
  return 'Click to view import details.'
}

export default function ImportManagementPage() {
  const { user } = useAuth()

  const [logs, setLogs] = useState<ImportLog[]>([])
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)

  // Import configuration
  const [importSettings, setImportSettings] = useState<ImportSettings | null>(null)
  const [configLoadError, setConfigLoadError] = useState('')
  const [configOpen, setConfigOpen] = useState(false)
  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set())
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set())
  const [configSaving, setConfigSaving] = useState(false)
  const [configSaveError, setConfigSaveError] = useState('')
  const [configSaveSuccess, setConfigSaveSuccess] = useState(false)

  // Cache status
  const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null)
  const [cacheLoading, setCacheLoading] = useState(true)
  const [cacheError, setCacheError] = useState('')
  const [deletingBundle, setDeletingBundle] = useState<string | null>(null)
  const [cacheOpen, setCacheOpen] = useState(false)

  // Sync/import action state
  const [activeAction, setActiveAction] = useState<'check' | 'reimport' | 'download' | null>(null)
  const [actionMessage, setActionMessage] = useState('')
  const [actionIsSuccess, setActionIsSuccess] = useState(true)
  const [aborting, setAborting] = useState(false)

  // Advanced options
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [serviceCode, setServiceCode] = useState('')

  // Force polling for 2 min after a trigger
  const [forcePoll, setForcePoll] = useState(false)
  const forcePollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Elapsed-time ticker while any import is running
  const [, setElapsedTick] = useState(0)

  // Import details modal
  const [selectedLog, setSelectedLog] = useState<ImportLog | null>(null)
  const [copySuccess, setCopySuccess] = useState(false)

  // Cached bundle details modal. Only the key is held — the info is derived from
  // cacheStatus, so a finished cache job's refresh shows up in the open modal.
  const [selectedBundleKey, setSelectedBundleKey] = useState<string | null>(null)

  // Background cache job (download / freshness check) for the selected bundle.
  // cacheJobRun is bumped when we start one, to (re)arm the polling effect.
  const [cacheJob, setCacheJob] = useState<CacheJobStatus | null>(null)
  // Which bundle cacheJob belongs to — the poll can outlive the open dialog (see
  // bgJobKey), so the dialog must not render another bundle's job as its own.
  const [cacheJobKey, setCacheJobKey] = useState<string | null>(null)
  // A job we keep watching after its dialog was closed. A cache download runs
  // server-side for minutes; closing the dialog must not abandon it, or the
  // Cached FCC Files panel would keep showing the pre-download size/expiry.
  const [bgJobKey, setBgJobKey] = useState<string | null>(null)
  const [cacheJobRun, setCacheJobRun] = useState(0)
  const [cacheJobStarting, setCacheJobStarting] = useState(false)
  const [cacheJobError, setCacheJobError] = useState('')

  // "Load from URL" modal (repopulate a bundle's cache from an alternate FCC path)
  const [loadUrlKey, setLoadUrlKey] = useState<string | null>(null)
  const [loadUrlValue, setLoadUrlValue] = useState('')
  const [loadUrlError, setLoadUrlError] = useState('')

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasRunning = logs.some((l) => l.status === 'running')
  const shouldPoll = hasRunning || forcePoll

  function startForcePoll() {
    setForcePoll(true)
    if (forcePollTimerRef.current) clearTimeout(forcePollTimerRef.current)
    forcePollTimerRef.current = setTimeout(() => {
      setForcePoll(false)
      forcePollTimerRef.current = null
    }, 120_000)
  }

  async function loadStatus() {
    try {
      const data = await getImportStatus(20)
      setLogs(data)
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load import status')
    } finally {
      setLoading(false)
    }
  }

  const loadCacheStatus = useCallback(async () => {
    setCacheError('')
    try {
      const data = await getCacheStatus()
      setCacheStatus(data)
    } catch (err) {
      setCacheError(err instanceof Error ? err.message : 'Failed to load cache status')
    } finally {
      setCacheLoading(false)
    }
  }, [])

  async function loadImportSettings() {
    setConfigLoadError('')
    try {
      const s = await getImportSettings()
      setImportSettings(s)
      setSelectedStates(new Set(s.monitored_states))
      setSelectedServices(new Set(Object.keys(s.monitored_services)))
    } catch (err) {
      setConfigLoadError(err instanceof Error ? err.message : 'Failed to load import configuration')
    }
  }

  useEffect(() => {
    loadStatus()
    loadCacheStatus()
    loadImportSettings()
  }, [loadCacheStatus])

  async function saveImportConfig() {
    setConfigSaving(true)
    setConfigSaveError('')
    setConfigSaveSuccess(false)
    try {
      await updateImportSettings(Array.from(selectedStates), Array.from(selectedServices))
      setConfigSaveSuccess(true)
      await loadImportSettings()
      setTimeout(() => setConfigSaveSuccess(false), 4000)
    } catch (err) {
      setConfigSaveError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setConfigSaving(false)
    }
  }

  // Polling: auto-refresh while any import is running or recently triggered
  useEffect(() => {
    if (shouldPoll) {
      if (!pollRef.current) {
        pollRef.current = setInterval(loadStatus, POLL_INTERVAL_MS)
      }
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [shouldPoll])

  // 1-second elapsed-time ticker
  useEffect(() => {
    if (!hasRunning) return
    const tickRef = setInterval(() => setElapsedTick((t) => t + 1), 1000)
    return () => clearInterval(tickRef)
  }, [hasRunning])

  // Track a bundle's cache job: pick up one that is already running (e.g. started
  // before a page reload), poll to completion, then refresh the cache list so the
  // new size/expiry shows.
  //
  // This deliberately watches `selectedBundleKey ?? bgJobKey` rather than only the
  // open dialog. A cache download runs on the server for minutes and survives the
  // dialog closing — which is exactly what the dialog tells the user — so closing
  // it must not abandon the watch, or the panel would keep showing the pre-download
  // size and expiry until a manual refresh.
  useEffect(() => {
    const key = selectedBundleKey ?? bgJobKey
    if (!key) {
      setCacheJob(null)
      setCacheJobKey(null)
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let sawRunning = false

    async function tick() {
      let job: CacheJobStatus | null = null
      try {
        job = await getCacheBundleJob(key!)
      } catch {
        // Transient — retry on the next tick rather than dropping the poll.
        timer = setTimeout(tick, CACHE_JOB_POLL_MS)
        return
      }
      if (cancelled) return
      setCacheJob(job)
      setCacheJobKey(key!)
      if (job?.status === 'running') {
        sawRunning = true
        timer = setTimeout(tick, CACHE_JOB_POLL_MS)
        return
      }
      if (sawRunning) {
        // A job we watched just finished — pick up the new size/expiry.
        loadCacheStatus()
      }
      // Settled: stop watching it in the background (the dialog, if open, keeps
      // showing the final status because cacheJob is still set).
      setBgJobKey((cur) => (cur === key ? null : cur))
    }
    tick()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [selectedBundleKey, bgJobKey, cacheJobRun, loadCacheStatus])

  // Close the bundle details dialog. A job in flight is handed off to the
  // background watcher above instead of blocking the close.
  function closeBundleDetails(key: string, jobInFlight: boolean) {
    if (jobInFlight) setBgJobKey(key)
    setSelectedBundleKey(null)
  }

  async function handleAbort() {
    if (!confirm('Mark the running import as failed? This clears the lock so a new import can start. The import process itself may still be running — check backend logs if unsure.')) return
    setAborting(true)
    try {
      await abortRunningImport()
      await loadStatus()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Abort failed')
    } finally {
      setAborting(false)
    }
  }

  function handleCopyError() {
    if (!selectedLog?.error_message) return
    navigator.clipboard.writeText(selectedLog.error_message).then(() => {
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    })
  }

  async function handleCheckForUpdates() {
    setActiveAction('check')
    setActionMessage('')
    try {
      const result = await triggerSync('update', { serviceCode: serviceCode || undefined })
      setActionIsSuccess(true)
      setActionMessage(result.message)
      if (result.triggered === false) {
        // Already up to date — cache might be populated now
        await loadCacheStatus()
        setTimeout(loadStatus, 500)
      } else {
        startForcePoll()
        setTimeout(loadStatus, 1000)
      }
    } catch (err) {
      setActionIsSuccess(false)
      setActionMessage(err instanceof Error ? err.message : 'Failed to trigger sync')
    } finally {
      setActiveAction(null)
    }
  }

  async function handleReimportFromCache() {
    setActiveAction('reimport')
    setActionMessage('')
    try {
      const result = await triggerImport('full', {
        serviceCode: serviceCode || undefined,
        useCachedOnly: true,
      })
      setActionIsSuccess(true)
      setActionMessage(result.message + ' — page will auto-refresh while running.')
      startForcePoll()
      setTimeout(loadStatus, 1000)
    } catch (err) {
      setActionIsSuccess(false)
      setActionMessage(err instanceof Error ? err.message : 'Failed to trigger import')
    } finally {
      setActiveAction(null)
    }
  }

  async function handleDownloadAndImport() {
    setActiveAction('download')
    setActionMessage('')
    try {
      const result = await triggerImport('full', {
        serviceCode: serviceCode || undefined,
        forceDownload: true,
      })
      setActionIsSuccess(true)
      setActionMessage(result.message + ' — page will auto-refresh while running.')
      startForcePoll()
      setTimeout(loadStatus, 1000)
    } catch (err) {
      setActionIsSuccess(false)
      setActionMessage(err instanceof Error ? err.message : 'Failed to trigger download')
    } finally {
      setActiveAction(null)
    }
  }

  async function handleDeleteBundle(bundleKey: string): Promise<boolean> {
    if (!confirm(`Delete cached file for ${bundleKey}?\n\nYou can re-download it by running "Check for Updates" or "Download & Import".`)) return false
    setDeletingBundle(bundleKey)
    try {
      await deleteCacheBundle(bundleKey)
      await loadCacheStatus()
      return true
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete cached bundle')
      return false
    } finally {
      setDeletingBundle(null)
    }
  }

  function openBundleDetails(bundleKey: string) {
    setCacheJobError('')
    setSelectedBundleKey(bundleKey)
  }

  function openLoadUrl(bundleKey: string) {
    setLoadUrlKey(bundleKey)
    // Prefill FCC's canonical URL for this bundle: usually the admin only needs
    // to tweak the path, and it documents where the file normally comes from.
    setLoadUrlValue(cacheStatus?.default_urls?.[bundleKey] ?? '')
    setLoadUrlError('')
  }

  /**
   * Kick off a background cache job and start polling it.
   * Returns the error message on failure, or null when the job was queued.
   */
  async function startCacheJob(start: () => Promise<CacheJobStatus>): Promise<string | null> {
    setCacheJobStarting(true)
    setCacheJobError('')
    try {
      const job = await start()
      setCacheJob(job)
      setCacheJobRun((n) => n + 1)
      return null
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start the cache job'
      setCacheJobError(message)
      return message
    } finally {
      setCacheJobStarting(false)
    }
  }

  async function handleCheckFreshness(bundleKey: string) {
    await startCacheJob(() => refreshCacheBundle(bundleKey))
  }

  async function handleLoadFromUrl() {
    if (!loadUrlKey) return
    const url = loadUrlValue.trim()
    // Client-side guard mirrors the server: http(s) on an fcc.gov host only.
    let host = ''
    try {
      const parsed = new URL(url)
      if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('scheme')
      host = parsed.hostname.toLowerCase()
    } catch {
      setLoadUrlError('Enter a valid http(s) URL.')
      return
    }
    if (host !== 'fcc.gov' && !host.endsWith('.fcc.gov')) {
      setLoadUrlError('URL must be on an fcc.gov domain.')
      return
    }
    const key = loadUrlKey
    setLoadUrlError('')
    // The download runs server-side; the details modal reports its progress.
    const error = await startCacheJob(() => loadCacheBundleFromUrl(key, url))
    if (error) {
      setLoadUrlError(error)
    } else {
      setLoadUrlKey(null)
      setSelectedBundleKey(key)
    }
  }

  const anyBusy = !!activeAction || hasRunning

  // Whether the cache has at least one populated bundle
  const hasCachedFiles = cacheStatus
    ? Object.values(cacheStatus.bundles).some((b) => b !== null)
    : false

  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">FCC Import Management</h1>

      {/* ── 0. Import Configuration ───────────────────────────────────── */}
      <div className="card mb-6">
        <button
          className="flex items-center justify-between w-full text-left group"
          onClick={() => setConfigOpen((o) => !o)}
          aria-expanded={configOpen}
        >
          <div className="flex items-center gap-2 min-w-0">
            <svg className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${configOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <h2 className="text-xl font-semibold shrink-0">Import Configuration</h2>
            {importSettings && !configOpen && (
              // Summarised, not enumerated: with all 50 states enabled the full
              // list ran to several lines and told the reader nothing.
              <span className="text-sm text-gray-500 truncate">
                {summariseSelection(importSettings.monitored_states, 'state')}
                {' · '}
                {summariseSelection(Object.keys(importSettings.monitored_services), 'service')}
              </span>
            )}
          </div>
          {/* Explicit affordance — the header being clickable was not discoverable. */}
          <span className="flex items-center gap-1 shrink-0 ml-3 text-sm text-blue-600 group-hover:text-blue-800 group-hover:underline">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {configOpen ? 'Hide settings' : 'Configure'}
          </span>
        </button>

        {configOpen && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
              Changes take effect on the next import. If you change monitored states or services,
              run a <strong>Re-import from Cache</strong> or <strong>Download &amp; Import</strong> to update your FCC data.
            </p>

            {configLoadError ? (
              <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                <span className="flex-1">{configLoadError}</span>
                <button
                  className="text-red-600 hover:text-red-800 underline shrink-0"
                  onClick={loadImportSettings}
                >
                  Retry
                </button>
              </div>
            ) : !importSettings ? (
              <p className="text-sm text-gray-400 italic">Loading configuration…</p>
            ) : (
              <>
                {/* States */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700">Monitored States</h3>
                    <div className="flex gap-3 text-xs">
                      <button className="tap text-primary-600 hover:underline" onClick={() => setSelectedStates(new Set(importSettings.all_states.map(s => s.code)))}>All</button>
                      <button className="tap text-primary-600 hover:underline" onClick={() => setSelectedStates(new Set())}>None</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                    {importSettings.all_states.map(({ code, name }) => (
                      <label key={code} className="flex items-center gap-1.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={selectedStates.has(code)}
                          onChange={(e) => {
                            const next = new Set(selectedStates)
                            if (e.target.checked) next.add(code); else next.delete(code)
                            setSelectedStates(next)
                          }}
                        />
                        <span className="text-xs text-gray-700 group-hover:text-gray-900" title={name}>{code}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Services */}
                <div className="mb-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Monitored Services</h3>
                  {Object.entries(importSettings.service_categories).map(([category, codes]) => (
                    <div key={category} className="mb-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 capitalize">{category.replace('_', ' ')}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {codes.map((code) => (
                          <label key={code} className="flex items-center gap-1.5 cursor-pointer group">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              checked={selectedServices.has(code)}
                              onChange={(e) => {
                                const next = new Set(selectedServices)
                                if (e.target.checked) next.add(code); else next.delete(code)
                                setSelectedServices(next)
                              }}
                            />
                            <span className="text-xs text-gray-700" title={importSettings.all_services[code]}>
                              <span className="font-mono">{code}</span> — {importSettings.all_services[code]}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {configSaveError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{configSaveError}</div>
                )}
                {configSaveSuccess && (
                  <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">Configuration saved.</div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={saveImportConfig}
                    disabled={configSaving || selectedStates.size === 0 || selectedServices.size === 0}
                    className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {configSaving ? 'Saving…' : 'Save Configuration'}
                  </button>
                  <span className="text-xs text-gray-400">
                    {selectedStates.size} state{selectedStates.size !== 1 ? 's' : ''} · {selectedServices.size} service{selectedServices.size !== 1 ? 's' : ''}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── 1. FCC Data Sync ──────────────────────────────────────────── */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold mb-4">FCC Data Sync</h2>

        {actionMessage && (
          <div className={`mb-4 ${actionIsSuccess ? 'alert alert-success' : 'alert alert-error'}`}>
            {actionMessage}
          </div>
        )}

        <div className="space-y-4">
          {/* Button 1: Check for Updates */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900">Check for Updates</p>
              <p className="text-sm text-gray-500 mt-0.5">
                Re-imports the weekly full dump if FCC has republished it, then applies every
                daily delta published since the full (catching up any missed day). Skips anything
                already imported, so it's safe to run anytime. Does nothing if fully up to date.
              </p>
            </div>
            <button
              className="btn btn-primary shrink-0"
              disabled={anyBusy}
              onClick={handleCheckForUpdates}
            >
              {activeAction === 'check' ? 'Starting…' : 'Check for Updates'}
            </button>
          </div>

          {/* Button 2: Re-import from Cache */}
          <div className={`flex flex-col sm:flex-row sm:items-start gap-3 p-4 border rounded-lg transition-colors ${hasCachedFiles ? 'border-gray-200 hover:border-primary-300' : 'border-gray-100 bg-gray-50'}`}>
            <div className="flex-1 min-w-0">
              <p className={`font-medium ${hasCachedFiles ? 'text-gray-900' : 'text-gray-400'}`}>Re-import from Cache</p>
              <p className="text-sm text-gray-500 mt-0.5">
                Skips the freshness check and FCC download. Imports from the most recently
                cached ZIP files. Use this after a code or schema fix when FCC data hasn't changed.
              </p>
              {!hasCachedFiles && !cacheLoading && (
                <p className="text-xs text-amber-600 mt-1 font-medium">
                  No cached files — run "Check for Updates" or "Download &amp; Import" first.
                </p>
              )}
            </div>
            <button
              className="btn btn-secondary shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={anyBusy || !hasCachedFiles}
              onClick={handleReimportFromCache}
              title={!hasCachedFiles ? 'No cached files available' : undefined}
            >
              {activeAction === 'reimport' ? 'Starting…' : 'Re-import from Cache'}
            </button>
          </div>

        </div>

        {/* Advanced options */}
        <div className="mt-4 border-t border-gray-100 pt-3">
          <button
            className="tap gap-1.5 text-sm text-gray-500 hover:text-gray-700"
            onClick={() => setShowAdvanced((v) => !v)}
            aria-expanded={showAdvanced}
          >
            <svg className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Advanced options
          </button>
          {showAdvanced && (
            <div className="mt-3 space-y-4">
              {/* Download & Import — under Advanced so a fresh full download isn't one stray click away */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">Download &amp; Import</p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Always downloads fresh ZIPs from FCC regardless of cache state, then imports.
                    Use only if you suspect the cache is corrupt or you need the absolute latest data.
                  </p>
                </div>
                <button
                  className="btn btn-secondary shrink-0"
                  disabled={anyBusy}
                  onClick={handleDownloadAndImport}
                >
                  {activeAction === 'download' ? 'Starting…' : 'Download & Import'}
                </button>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="label">Limit to service (optional)</label>
                  <select
                    className="input"
                    value={serviceCode}
                    onChange={(e) => setServiceCode(e.target.value)}
                  >
                    <option value="">All services</option>
                    {Object.entries(RADIO_SERVICES).map(([code, desc]) => (
                      <option key={code} value={code}>{code} — {desc}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Running import status — live progress shown inline so you don't have to scroll */}
        {hasRunning && (() => {
          const runningLog = logs.find((l) => l.status === 'running')!
          const isStuck = runningLog.import_started_at
            && Date.now() - new Date(runningLog.import_started_at).getTime() > 4 * 60 * 60 * 1000
          const stuckHours = runningLog.import_started_at
            ? Math.floor((Date.now() - new Date(runningLog.import_started_at).getTime()) / (60 * 60 * 1000))
            : 0

          const elapsed = formatElapsed(runningLog.import_started_at)
          const phaseLabel = runningLog.current_phase
            ? (PHASE_LABELS[runningLog.current_phase] ?? runningLog.current_phase)
            : null
          const isDownloading = runningLog.current_phase === 'downloading'
          const dlTotal = runningLog.download_bytes_total
          const dlReceived = runningLog.download_bytes_received
          const dlPct = isDownloading && dlTotal && dlTotal > 0 && dlReceived !== null
            ? Math.min(100, Math.round((dlReceived / dlTotal) * 100))
            : null

          // ETA + overall progress percent, both derived from how long
          // comparable past imports took (same file, else same type). The
          // percent is capped just under 100% until the run actually finishes so
          // the bar never sits full while still working; when there's no
          // comparable history yet we show an indeterminate (animated) bar.
          let etaLabel: string | null = null
          let progressPct: number | null = null
          let progressIndeterminate = false
          if (runningLog.import_started_at) {
            const startMs = parseServerDate(runningLog.import_started_at)?.getTime() ?? Date.now()
            const elapsedSecs = (Date.now() - startMs) / 1000
            const sameFile = logs.filter(l => l.status === 'completed' && l.file_name === runningLog.file_name && l.duration_seconds)
            const completed = sameFile.length > 0
              ? sameFile
              : logs.filter(l => l.status === 'completed' && l.import_type === runningLog.import_type && l.duration_seconds)
            if (completed.length > 0) {
              const sample = completed.slice(0, 5)
              const avg = sample.reduce((s, l) => s + l.duration_seconds!, 0) / sample.length
              const remainingSecs = Math.max(0, avg - elapsedSecs)
              if (remainingSecs > 0) etaLabel = `~${formatDuration(Math.round(remainingSecs))} remaining`
              else if (elapsedSecs > avg * 1.1) etaLabel = 'finishing up…'
              progressPct = Math.max(3, Math.min(99, Math.round((elapsedSecs / avg) * 100)))
            } else {
              progressIndeterminate = true
            }
          }
          // During the download phase prefer the real byte-based percentage;
          // otherwise use the time-based estimate for the full-width meter.
          const barPct = dlPct !== null ? dlPct : progressPct

          return (
            <div className="mt-4 border-t border-blue-100 pt-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {statusBadge('running')}
                <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="font-medium text-blue-700">{phaseLabel ?? 'Starting…'}</span>
                <span className="text-gray-500 text-xs">Import #{runningLog.id}</span>
                {elapsed && <span className="text-gray-500 text-xs">· {elapsed} elapsed</span>}
                {etaLabel && <span className="text-gray-500 text-xs">· {etaLabel}</span>}
                {barPct !== null && <span className="text-gray-500 text-xs">· {barPct}%</span>}
              </div>
              {/* Full-width progress meter across the row */}
              {barPct !== null ? (
                <div
                  className="w-full bg-gray-200 rounded-full h-2 overflow-hidden"
                  role="progressbar"
                  aria-valuenow={barPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Import progress"
                >
                  <div
                    className="h-2 rounded-full bg-blue-500 transition-all duration-700 ease-out"
                    style={{ width: `${barPct}%` }}
                  />
                </div>
              ) : progressIndeterminate ? (
                <div
                  className="w-full bg-gray-200 rounded-full h-2 overflow-hidden"
                  role="progressbar"
                  aria-label="Import in progress"
                >
                  {/* No historical estimate yet — indeterminate animated bar */}
                  <div className="h-2 w-1/3 rounded-full bg-blue-400 animate-pulse" />
                </div>
              ) : null}
              {dlPct !== null && dlTotal && dlReceived !== null && (
                <div className="text-xs text-gray-500">
                  {formatMB(dlReceived)} / {formatMB(dlTotal)} downloaded
                </div>
              )}
              {isStuck && (
                <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded px-3 py-2 text-sm flex items-center justify-between gap-3 flex-wrap">
                  <span>
                    ⚠ This import may be stalled — started {stuckHours} hour{stuckHours !== 1 ? 's' : ''} ago.
                    {' '}If the process crashed, use Abort to clear the lock and start a new import.
                  </span>
                  <button
                    onClick={handleAbort}
                    disabled={aborting}
                    className="shrink-0 px-3 py-1 text-xs font-medium bg-amber-700 text-white rounded hover:bg-amber-800 disabled:opacity-50"
                  >
                    {aborting ? 'Aborting…' : 'Abort Import'}
                  </button>
                </div>
              )}
            </div>
          )
        })()}
      </div>

      {/* ── 2. Cached FCC Files ───────────────────────────────────────── */}
      <div className="card mb-6">
        <div className="flex items-center justify-between">
          <button
            className="flex items-center gap-2 text-left"
            onClick={() => setCacheOpen((o) => !o)}
            aria-expanded={cacheOpen}
          >
            <svg className={`h-5 w-5 text-gray-400 transition-transform ${cacheOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <h2 className="text-xl font-semibold">Cached FCC Files</h2>
            <span className="text-sm text-gray-500">
              {cacheLoading
                ? 'loading…'
                : hasCachedFiles
                  ? `${Object.values(cacheStatus!.bundles).filter(Boolean).length} cached · ${formatBytes(cacheStatus!.total_size_bytes)}`
                  : 'none cached'}
            </span>
          </button>
          {cacheOpen && (
            <button className="btn btn-secondary text-sm px-3 py-1" onClick={loadCacheStatus}>
              Refresh
            </button>
          )}
        </div>

        {cacheOpen && (<>
        {cacheError && <div className="alert alert-error mb-3 mt-3">{cacheError}</div>}

        {cacheLoading ? (
          <p className="text-sm text-gray-400 italic">Loading…</p>
        ) : !cacheStatus ? null : (
          <div className="overflow-x-auto">
            <p className="text-xs text-gray-500 mb-2">
              Click any row to see its details and to check freshness, load it from an alternate
              FCC URL, or delete it.
            </p>
            <div className="table-wrap">
              <table className="table text-sm">
                <thead>
                  <tr>
                    <th>Bundle</th>
                    <th>Size</th>
                    <th>Cached</th>
                    <th>Age</th>
                    <th>Expires in</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(cacheStatus.bundles).map(([key, info]: [string, CacheBundleInfo | null]) => (
                    <tr
                      key={key}
                      className={`group cursor-pointer hover:bg-gray-50 ${info ? '' : 'text-gray-400'}`}
                      onClick={() => openBundleDetails(key)}
                    >
                      <td className="font-mono text-xs">{bundleLabel(key)}</td>
                      <td>{info ? formatBytes(info.size_bytes) : 'not cached'}</td>
                      <td>
                        {info?.downloaded_at
                          ? format(parseServerDate(info.downloaded_at)!, 'MM/dd/yy HH:mm')
                          : '—'}
                      </td>
                      <td>{info ? `${info.age_days.toFixed(1)}d` : '—'}</td>
                      <td>
                        {info
                          ? info.expires_in_days === null
                            ? <span className="text-gray-500">retained</span>
                            : info.expires_in_days > 0
                              ? `${info.expires_in_days.toFixed(1)}d`
                              : <span className="text-red-500">expired</span>
                          : '—'}
                      </td>
                      <td className="whitespace-nowrap text-right">
                        <span
                          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium
                                     text-blue-600 bg-blue-50 group-hover:bg-blue-100 group-hover:text-blue-800"
                          aria-hidden="true"
                        >
                          Details
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-2">
          The 4 weekly bundles are retained indefinitely — each is replaced when a newer copy
          downloads, never deleted on a timer, so a cached copy is always available if FCC's own
          download breaks. Daily deltas are evicted after 7 days.
          "Re-import from Cache" uses these files — delete and re-download if you suspect corruption.
        </p>
        </>)}
      </div>

      {/* ── 3. Recent Imports ─────────────────────────────────────────── */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Recent Imports</h2>
          <button className="btn btn-secondary text-sm px-3 py-1" onClick={loadStatus}>
            Refresh
          </button>
        </div>

        {loadError && <div className="alert alert-error mb-4">{loadError}</div>}

        {loading ? (
          <div className="text-gray-500">Loading…</div>
        ) : logs.length === 0 ? (
          <p className="text-gray-500">No imports have been run yet.</p>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-2">Click any row for full details.</p>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>File</th>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Processed</th>
                    <th>Inserted</th>
                    <th>Errors</th>
                    <th>Size</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const isRunning = log.status === 'running'
                    const elapsed = isRunning ? formatElapsed(log.import_started_at) : null
                    const phaseLabel = isRunning && log.current_phase
                      ? (PHASE_LABELS[log.current_phase] ?? log.current_phase)
                      : null
                    const isDownloading = log.current_phase === 'downloading'
                    const dlTotal = log.download_bytes_total
                    const dlReceived = log.download_bytes_received
                    const dlPct = isDownloading && dlTotal && dlTotal > 0 && dlReceived !== null
                      ? Math.min(100, Math.round((dlReceived / dlTotal) * 100))
                      : null

                    let etaLabel: string | null = null
                    if (isRunning && log.import_started_at) {
                      const sameFile = logs.filter(l => l.status === 'completed' && l.file_name === log.file_name && l.duration_seconds)
                      const completed = sameFile.length > 0
                        ? sameFile
                        : logs.filter(l => l.status === 'completed' && l.import_type === log.import_type && l.duration_seconds)
                      if (completed.length > 0) {
                        const sample = completed.slice(0, 5)
                        const avg = sample.reduce((s, l) => s + l.duration_seconds!, 0) / sample.length
                        const startMs = parseServerDate(log.import_started_at)?.getTime() ?? Date.now()
                        const elapsedSecs = (Date.now() - startMs) / 1000
                        const remainingSecs = Math.max(0, avg - elapsedSecs)
                        if (remainingSecs > 0) {
                          etaLabel = `~${formatDuration(Math.round(remainingSecs))} remaining`
                        } else if (elapsedSecs > avg * 1.1) {
                          etaLabel = 'finishing up…'
                        }
                      }
                    }

                    return (
                      <React.Fragment key={log.id}>
                        <tr
                          className={[
                            'cursor-pointer',
                            isRunning ? 'bg-blue-50 hover:bg-blue-100' : (log.status === 'failed' || log.status === 'interrupted') ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50',
                          ].join(' ')}
                          onClick={() => { setCopySuccess(false); setSelectedLog(log) }}
                          title={rowTooltip(log)}
                        >
                          <td className="text-gray-500 text-sm">{log.id}</td>
                          <td><span className="font-mono text-sm">{log.import_type}</span></td>
                          <td className="text-sm max-w-xs truncate font-mono" title={log.file_name}>{log.file_name}</td>
                          <td className="text-sm">
                            {log.import_started_at
                              ? format(parseServerDate(log.import_started_at)!, 'MM/dd/yy HH:mm')
                              : '—'}
                          </td>
                          <td className="text-sm">
                            {isRunning
                              ? <span className="text-blue-700">{elapsed || '…'}</span>
                              : formatDuration(log.duration_seconds)}
                          </td>
                          <td className="text-sm">{log.records_processed?.toLocaleString() ?? '—'}</td>
                          <td className="text-sm">{log.records_inserted?.toLocaleString() ?? '—'}</td>
                          <td className="text-sm text-red-600">
                            {log.records_errors ? log.records_errors.toLocaleString() : '—'}
                          </td>
                          <td className="text-sm">{formatBytes(log.file_size_bytes)}</td>
                          <td>
                            {isRunning
                              ? <span className="flex items-center gap-1">{statusBadge(log.status)}<span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /></span>
                              : statusBadge(log.status)}
                          </td>
                        </tr>
                        {isRunning && (phaseLabel || dlPct !== null || etaLabel) && (
                          <tr key={`${log.id}-progress`} className="bg-blue-50">
                            {/* whitespace-normal has to sit on the div: `.table td`
                                outranks the utility on the cell itself (see PanelRow
                                in UsersPage). */}
                            <td colSpan={10} className="pb-2 px-4">
                              <div className="whitespace-normal flex flex-wrap items-center gap-4 text-xs text-blue-700">
                                {phaseLabel && <span className="font-medium">{phaseLabel}</span>}
                                {etaLabel && <span className="text-gray-500">{etaLabel}</span>}
                                {dlPct !== null && dlTotal && dlReceived !== null && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-32 bg-gray-200 rounded-full h-1.5">
                                      <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${dlPct}%` }} />
                                    </div>
                                    <span className="text-gray-500">{formatMB(dlReceived)} / {formatMB(dlTotal)}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Import Details Modal ───────────────────────────────────────── */}
      {selectedLog && (
        <Modal
          titleId="import-detail-title"
          title={`Import #${selectedLog.id} — ${selectedLog.file_name}`}
          subtitle={selectedLog.file_url ?? undefined}
          onClose={() => setSelectedLog(null)}
          showCloseButton
          maxWidth="max-w-2xl"
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
            <div>
              <span className="text-gray-500">Status</span>
              <div className="mt-0.5">{statusBadge(selectedLog.status)}</div>
            </div>
            <div>
              <span className="text-gray-500">Type</span>
              <div className="font-mono mt-0.5">{selectedLog.import_type}</div>
            </div>
            <div>
              <span className="text-gray-500">Started</span>
              <div className="mt-0.5">
                {selectedLog.import_started_at
                  ? format(parseServerDate(selectedLog.import_started_at)!, 'yyyy-MM-dd HH:mm:ss')
                  : '—'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Completed</span>
              <div className="mt-0.5">
                {selectedLog.import_completed_at
                  ? format(parseServerDate(selectedLog.import_completed_at)!, 'yyyy-MM-dd HH:mm:ss')
                  : '—'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Duration</span>
              <div className="mt-0.5">{formatDuration(selectedLog.duration_seconds)}</div>
            </div>
            <div>
              <span className="text-gray-500">File size</span>
              <div className="mt-0.5">{formatBytes(selectedLog.file_size_bytes)}</div>
            </div>
            <div>
              <span className="text-gray-500">Processed</span>
              <div className="mt-0.5">{selectedLog.records_processed?.toLocaleString() ?? '—'}</div>
            </div>
            <div>
              <span className="text-gray-500">Inserted</span>
              <div className="mt-0.5">{selectedLog.records_inserted?.toLocaleString() ?? '—'}</div>
            </div>
            <div>
              <span className="text-gray-500">Updated</span>
              <div className="mt-0.5">{selectedLog.records_updated?.toLocaleString() ?? '—'}</div>
            </div>
            <div>
              <span className="text-gray-500">Skipped</span>
              <div className="mt-0.5">{selectedLog.records_skipped?.toLocaleString() ?? '—'}</div>
            </div>
            <div>
              <span className="text-gray-500 text-red-600">Errors</span>
              <div className="mt-0.5 text-red-600">
                {selectedLog.records_errors?.toLocaleString() ?? '—'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Triggered by</span>
              <div className="mt-0.5">{selectedLog.imported_by ?? '—'}</div>
            </div>
          </div>

          {selectedLog.file_url && (
            <div className="text-sm mb-4">
              <span className="text-gray-500">Source URL</span>
              <div className="font-mono text-xs break-all mt-0.5 text-blue-700">
                {selectedLog.file_url}
              </div>
            </div>
          )}

          {selectedLog.file_hash && (
            <div className="text-sm mb-4">
              <span className="text-gray-500">SHA-256</span>
              <div className="font-mono text-xs break-all mt-0.5 text-gray-600">
                {selectedLog.file_hash}
              </div>
            </div>
          )}

          {selectedLog.error_message && (
            <div className="mt-2">
              <p className="text-sm font-medium text-red-700 mb-1">Error</p>
              <pre className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3 whitespace-pre-wrap break-words select-all font-mono max-h-64 overflow-y-auto">
                {selectedLog.error_message}
              </pre>
            </div>
          )}

          <ModalFooter>
            {selectedLog.error_message && (
              <button
                className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700"
                onClick={handleCopyError}
              >
                {copySuccess ? '✓ Copied' : 'Copy Error'}
              </button>
            )}
            <button
              className="px-3 py-1.5 text-sm rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
              onClick={() => setSelectedLog(null)}
            >
              Close
            </button>
          </ModalFooter>
        </Modal>
      )}

      {/* ── Cached Bundle Details Modal ────────────────────────────────── */}
      {selectedBundleKey && (() => {
        const key = selectedBundleKey
        const info = cacheStatus?.bundles?.[key] ?? null
        const defaultUrl = cacheStatus?.default_urls?.[key] ?? null
        // Only this bundle's job — the poll can outlive the dialog for a different key.
        const thisJob = cacheJobKey === key ? cacheJob : null
        const jobRunning = thisJob?.status === 'running'
        // `busy` disables the ACTIONS (you can't start a second job on the same
        // bundle). It must never disable closing: the job runs server-side for
        // minutes and the dialog explicitly promises you can walk away from it.
        const busy = jobRunning || cacheJobStarting
        return (
        <Modal
          titleId="bundle-detail-title"
          title={`Cached bundle — ${bundleLabel(key)}`}
          subtitle={info?.url ?? defaultUrl ?? undefined}
          onClose={() => closeBundleDetails(key, busy)}
          showCloseButton
          maxWidth="max-w-2xl"
        >
          {!info ? (
            <p className="text-sm text-gray-600 mb-4">
              Nothing is cached for <span className="font-mono">{key}.zip</span> yet.
              {defaultUrl
                ? ' Check freshness to download FCC’s current copy, or load it from an alternate URL.'
                : ' Load it from a URL to populate this slot.'}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
                <div>
                  <span className="text-gray-500">Bundle</span>
                  <div className="mt-0.5">{bundleLabel(key)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Key</span>
                  <div className="font-mono mt-0.5">{key}</div>
                </div>
                <div>
                  <span className="text-gray-500">Filename</span>
                  <div className="font-mono mt-0.5 break-all">{info.filename}</div>
                </div>
                <div>
                  <span className="text-gray-500">Size</span>
                  <div className="mt-0.5">{formatBytes(info.size_bytes)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Cached</span>
                  <div className="mt-0.5">
                    {info.downloaded_at
                      ? format(parseServerDate(info.downloaded_at)!, 'yyyy-MM-dd HH:mm:ss')
                      : '—'}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Age</span>
                  <div className="mt-0.5">{`${info.age_days.toFixed(1)}d`}</div>
                </div>
                <div>
                  <span className="text-gray-500">Last verified with FCC</span>
                  <div className="mt-0.5">
                    {info.verified_at
                      ? format(parseServerDate(info.verified_at)!, 'yyyy-MM-dd HH:mm:ss')
                      : <span className="text-gray-400">never</span>}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Expires in</span>
                  <div className="mt-0.5">
                    {info.expires_in_days === null
                      ? <span className="text-gray-500">Retained (never evicted)</span>
                      : info.expires_in_days > 0
                        ? `${info.expires_in_days.toFixed(1)}d`
                        : <span className="text-red-500">expired</span>}
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">ETag</span>
                  <div className="font-mono text-xs mt-0.5 break-all">{info.etag ?? '—'}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">FCC Last-Modified</span>
                  <div className="font-mono text-xs mt-0.5 break-all">{info.last_modified ?? '—'}</div>
                </div>
              </div>

              <div className="text-sm mb-4">
                <span className="text-gray-500">Source URL</span>
                <div className="font-mono text-xs break-all mt-0.5 text-blue-700">
                  {info.url ?? '—'}
                </div>
              </div>
            </>
          )}

          {/* Background job status (freshness check / download) */}
          {cacheJobError && <div className="alert alert-error text-sm mb-3">{cacheJobError}</div>}
          {thisJob && (
            <div
              className={`text-sm rounded border px-3 py-2 mb-3 ${
                thisJob.status === 'running'
                  ? 'bg-blue-50 border-blue-200 text-blue-800'
                  : thisJob.status === 'completed'
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-700'
              }`}
            >
              <span className="font-medium">
                {thisJob.operation === 'refresh' ? 'Freshness check' : 'Download'}
                {thisJob.status === 'running'
                  ? ' running…'
                  : thisJob.status === 'completed' ? ' complete' : ' failed'}
              </span>
              {thisJob.status === 'running' && (
                <span className="block text-xs mt-0.5">
                  This runs on the server and can take several minutes for a full bundle —
                  it keeps going even if you close this dialog.
                </span>
              )}
              {thisJob.message && <span className="block mt-1">{thisJob.message}</span>}
            </div>
          )}

          <ModalFooter>
            {info && (
              <button
                className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 mr-auto"
                disabled={deletingBundle === key || busy || hasRunning}
                onClick={async () => {
                  if (await handleDeleteBundle(key)) setSelectedBundleKey(null)
                }}
              >
                {deletingBundle === key ? 'Deleting…' : 'Delete cached file'}
              </button>
            )}
            <button
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={busy || hasRunning || !defaultUrl}
              onClick={() => handleCheckFreshness(key)}
              title={
                defaultUrl
                  ? 'Ask FCC whether this file has changed. Unchanged extends the cache expiry; newer downloads it.'
                  : 'This key has no canonical FCC URL — use Load from URL.'
              }
            >
              {busy && thisJob?.operation === 'refresh' ? 'Checking…' : 'Check freshness'}
            </button>
            <button
              className="px-3 py-1.5 text-sm rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
              disabled={busy || hasRunning}
              onClick={() => openLoadUrl(key)}
              title="Download this bundle from an alternate FCC URL into the cache"
            >
              Load from URL…
            </button>
            <button
              className="px-3 py-1.5 text-sm rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
              onClick={() => closeBundleDetails(key, busy)}
            >
              Close
            </button>
          </ModalFooter>
        </Modal>
        )
      })()}

      {loadUrlKey && (
        <Modal
          titleId="load-url-title"
          title={`Load from URL — ${bundleLabel(loadUrlKey)}`}
          subtitle="Download this bundle from an alternate FCC path into the cache"
          onClose={() => { if (!cacheJobStarting) setLoadUrlKey(null) }}
          showCloseButton
          maxWidth="max-w-xl"
        >
          <p className="text-sm text-gray-600 mb-3">
            Use this when FCC's normal download is unavailable (maintenance / broken build) but the
            file is reachable at a different FCC path. It's validated as a ZIP and stored as{' '}
            <span className="font-mono">{loadUrlKey}.zip</span>, so the next import/check uses it.
            Must be an <span className="font-mono">https://…fcc.gov</span> URL. The download runs
            in the background — progress is reported in the bundle's details dialog.
          </p>
          <label className="block text-sm font-medium mb-1" htmlFor="load-url-input">
            Alternate FCC URL
          </label>
          <input
            id="load-url-input"
            type="url"
            className="input w-full font-mono text-xs"
            placeholder={cacheStatus?.default_urls?.[loadUrlKey] ?? 'https://…fcc.gov/…/file.zip'}
            value={loadUrlValue}
            onChange={(e) => setLoadUrlValue(e.target.value)}
            disabled={cacheJobStarting}
            autoFocus
          />
          {cacheStatus?.default_urls?.[loadUrlKey] && (
            <p className="text-xs text-gray-500 mt-1">
              Prefilled with this bundle's normal FCC location — edit it to point at the copy you found.
            </p>
          )}
          {loadUrlError && <div className="alert alert-error mt-3 text-sm">{loadUrlError}</div>}
          <ModalFooter>
            <button
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 mr-auto"
              disabled={cacheJobStarting || !loadUrlValue.trim()}
              onClick={handleLoadFromUrl}
            >
              {cacheJobStarting ? 'Starting…' : 'Download to cache'}
            </button>
            <button
              className="px-3 py-1.5 text-sm rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
              disabled={cacheJobStarting}
              onClick={() => setLoadUrlKey(null)}
            >
              Cancel
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}
